import React, { useState } from 'react'
import { useUser } from 'util/UserContext'
import { Info, Loader2 } from 'lucide-react'
import Modal from 'ui/Modal'
import InputField from 'ui/InputField'
import api from 'config/axiosConfig'
import { toast } from 'sonner'
import { apiRoutes } from "util/apiRoutes";

interface Props {
    onClose: () => void
}

const DISPLAYNAME_REQUIREMENTS = {
    maxLength: 64,
    minLength: 0
};

const UserDisplaynameModal: React.FC<Props> = ({ onClose }) => {
    const { currentUser, refreshUser } = useUser()
    const [displayName, setDisplayName] = useState<string>(currentUser?.display_name || '');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        if (value.length > DISPLAYNAME_REQUIREMENTS.maxLength) {
            return;
        }
        setDisplayName(value);
        setError(null);
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        if (displayName.length > DISPLAYNAME_REQUIREMENTS.maxLength) {
            setError(`Display name must be fewer than ${DISPLAYNAME_REQUIREMENTS.maxLength} characters long.`);
            setIsSubmitting(false);
            return;
        }

        try {
            await api.patch(apiRoutes.user.displayname, { display_name: displayName });
            await refreshUser();
            if (currentUser?.display_name) {
                currentUser.display_name = displayName;
            }
            toast.success('Display name updated successfully!');
            onClose();
        } catch (error) {
            console.error('Error updating display name:', error);
            setError('Failed to update display name. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleClose = (): void => {
        onClose();
    }

    const isButtonDisabled = isSubmitting || displayName.length > DISPLAYNAME_REQUIREMENTS.maxLength;

    const footer = (
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
            <button
                type="button"
                onClick={onClose}
                className="btn-secondary w-full sm:w-fit py-2 px-4"
            >
                Cancel
            </button>
            <button
                type="submit"
                onClick={handleSubmit}
                disabled={isButtonDisabled}
                className={`w-full sm:w-fit py-2 px-4 ${
                    isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
            </button>
        </div>
    );

    return (
        <Modal isOpen={true} onClose={handleClose} title="Name" footer={footer} stickyFooter>
            <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        What people should call you. This will be shown on your profile.
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
                        <div className="form-control">
                            <InputField
                                type="text"
                                id="displayName"
                                name="displayName"
                                labelText="Name"
                                fieldPlaceholder={currentUser?.display_name}
                                value={displayName}
                                onChange={handleDisplayNameChange}
                                spellCheck={false}
                                maxLength={DISPLAYNAME_REQUIREMENTS.maxLength}
                            />
                        </div>

                        {error && (
                            <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                <Info className="mr-1 size-4" />
                                <span>{error}</span>
                            </div>
                        )}
                    </form>
                </div>

                {/* Action buttons moved to Modal footer */}
            </div>
        </Modal>
    );
};

export default UserDisplaynameModal; 